import { DateTime } from 'luxon';
import type { IVehicleTaxResult } from '../models/result.model';
import type { IVehicleTaxParameters } from '../models/parameters.model';
import type { IVehicleTaxConstants } from '../constants/vehicle-tax.constants';
import { FuelType } from '../models/fuel-type.enum';
import { EnvironmentalClass } from '../models/environmental-class.enum';

export class VehicleTaxCalculator {
  public static getBonusMalus(emissionsCo2PerKm: number, fuelType: FuelType, registrationDate: DateTime, currentDate: DateTime): number {
    const age: number = Math.floor(currentDate.diff(registrationDate, 'years').years);

    if (age <= 3 && (fuelType === FuelType.Diesel || fuelType === FuelType.Petrol)) {
      let malusTax: number = 0;
      if (registrationDate >= DateTime.fromISO('2018-07-01') && registrationDate < DateTime.fromISO('2021-04-01') && emissionsCo2PerKm >= 95) {
        if (emissionsCo2PerKm > 140) {
          malusTax += (emissionsCo2PerKm - 140) * 107;
          malusTax += 45 * 82;
        } else {
          malusTax += (emissionsCo2PerKm - 95) * 82;
        }
      } else if (registrationDate >= DateTime.fromISO('2021-04-01') && registrationDate < DateTime.fromISO('2022-06-01') && emissionsCo2PerKm >= 90) {
        if (emissionsCo2PerKm > 130) {
          malusTax += (emissionsCo2PerKm - 130) * 132;
          malusTax += 40 * 107;
        } else {
          malusTax += (emissionsCo2PerKm - 90) * 107;
        }
      } else if (registrationDate >= DateTime.fromISO('2022-06-01') && emissionsCo2PerKm >= 75) {
        if (emissionsCo2PerKm > 125) {
          malusTax += (emissionsCo2PerKm - 125) * 132;
          malusTax += 50 * 107;
        } else {
          malusTax += (emissionsCo2PerKm - 75) * 107;
        }
      }
      return malusTax;
    }
    return 0;
  }

  public static calculateCarbonEmissionsTax(euroClass: EnvironmentalClass, currentDate: DateTime, registrationDate: DateTime, fuelType: FuelType, taxedEmissionsGCoPerKm: number): number {
    const age: number = Math.floor(currentDate.diff(registrationDate, 'years').years);
    let taxPayment = 0;

    if (euroClass === EnvironmentalClass.Euro4 && age > 3 && (fuelType === FuelType.Diesel || fuelType === FuelType.Petrol)) {
      taxPayment += taxedEmissionsGCoPerKm * 22;
    }
    if (fuelType === FuelType.Other) {
      taxPayment += taxedEmissionsGCoPerKm * 11;
    }

    return taxPayment;
  }

  public static calculateDieselTaxes(fuelType: FuelType, registrationDate: DateTime, emissionsCo2PerKm: number, euroClass: EnvironmentalClass, taxedEmissionsGCoPerKm: number): number {
    let dieselTax = 0;

    if (fuelType === FuelType.Diesel) {
      // Fuel add on tax
      if (registrationDate >= DateTime.fromISO('2018-07-01')) {
        dieselTax += emissionsCo2PerKm * 13.52;
      } else if (euroClass === EnvironmentalClass.Euro4) {
        dieselTax += (taxedEmissionsGCoPerKm * 22 + 360) * 2.37;
      }

      // Environmental add on tax
      if (registrationDate >= DateTime.fromISO('2008-01-01')) {
        dieselTax += 250;
      } else if (euroClass === EnvironmentalClass.Euro4) {
        dieselTax += 500;
      }
    }
    return dieselTax;
  }

  public static getEuro3Tax(weightKg: number, fuelType: FuelType, taxConstants: IVehicleTaxConstants): number {
    let sanitizedWeightKg: number = weightKg;

    sanitizedWeightKg = sanitizedWeightKg < 0 ? 0 : sanitizedWeightKg;
    sanitizedWeightKg = sanitizedWeightKg > 3500 ? 3500 : sanitizedWeightKg;
    const tax = taxConstants[fuelType].find((b) => sanitizedWeightKg >= b.lowerLimitKg && sanitizedWeightKg <= b.upperLimitKg);
    return tax?.euro3TaxSek || 0;
  }

  public static calculateCost(params: IVehicleTaxParameters, taxConstants: IVehicleTaxConstants): IVehicleTaxResult {
    const age: number = Math.floor(params.currentDate.diff(params.registrationDate, 'years').years);
    const weightKg = params.weightKg ? params.weightKg : 1000;
    const taxedEmissionsGCoPerKm = params.emissionsCo2PerKm - 111 >= 0 ? params.emissionsCo2PerKm - 111 : 0;
    const euroClass = params.registrationDate <= DateTime.fromISO('2006-01-01') && params.weightKg ? EnvironmentalClass.Euro3 : EnvironmentalClass.Euro4;

    // Tax free older than 30 years
    if (age > 30) {
      return {
        costInSek: 0,
      };
    }

    // Base tax
    let taxPayment = euroClass === EnvironmentalClass.Euro4 ? 360 : 0;

    // Bonus malus tax
    taxPayment += this.getBonusMalus(params.emissionsCo2PerKm, params.fuelType, params.registrationDate, params.currentDate);

    // Carbon emissions tax
    taxPayment += this.calculateCarbonEmissionsTax(euroClass, params.currentDate, params.registrationDate, params.fuelType, taxedEmissionsGCoPerKm);

    // Diesel taxes
    taxPayment += this.calculateDieselTaxes(params.fuelType, params.registrationDate, params.emissionsCo2PerKm, euroClass, taxedEmissionsGCoPerKm);

    // Before 2006 based on weightKg and euro 4 environmental class both diesel and petrol. Table available per weightKg of car and fuel type
    if (!(euroClass === EnvironmentalClass.Euro4)) {
      taxPayment = this.getEuro3Tax(weightKg, params.fuelType, taxConstants);
    }

    return {
      costInSek: Math.round(taxPayment),
    };
  }
}

